<template>
    <div>
        <div class="flex items-center justify-start md:justify-between flex-wrap py-4 gap-x-2">
            <div class="sm:flex-auto">
                <h1 class="text-xl font-semibold text-gray-900 select-none">{{ searchParams.archived_filter ? "Archived" : "" }} Inspections</h1>
            </div>
            <div class="flex items-center">
                <button
                    class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                    @click.prevent="scheduleInspection">
                    New Inspection
                </button>

                <div class="btn-group ml-2">
                    <simple-dropdown label="Options">
                        <template slot="buttonOptions">
                            <simple-dropdown-option
                                value="Show Archived Inspections"
                                key="archived_inspections"
                                @click="toggleArchivedInspections">
                                {{ searchParams.archived_filter ? "Show Current Inspections" : "Show Archived Inspections" }}
                            </simple-dropdown-option>
                            <simple-dropdown-option
                                value="Export Inspections"
                                key="export_inspections"
                                @click="exportInspections">
                                Export Inspections
                            </simple-dropdown-option>
                            <div class="border-b border-gray-200 my-2"></div>
                            <simple-dropdown-option
                                value="Settings"
                                key="inspection_settings"
                                v-if="userCan('manage-inspections')"
                                @click="openLinkInNewTab('/settings/inspections')">
                                Inspection Settings
                            </simple-dropdown-option>
                        </template>
                    </simple-dropdown>
                </div>
            </div>
        </div>

        <expandable-filters-container
            :showExpandOption="true"
            filter-button-text="More Filters"
            @apply-filters="fetchInspections()"
            @reset-filters="clearFilters">
            <div slot="top-panel-left">
                <div class="hidden sm:flex sm:justify-start sm:items-center sm:flex-wrap sm:gap-2">
                    <simple-dropdown :label="selectedInspector ? selectedInspector.name : 'All Inspectors'"
                                     v-model="searchParams.inspector_id" origin="bottom-start"
                                     @input="fetchInspections">
                        <template slot="buttonOptions">
                            <simple-dropdown-option
                                :key="null"
                                :value="undefined">
                                All Inspectors
                            </simple-dropdown-option>
                            <simple-dropdown-option
                                v-for="inspector in inspectors"
                                :key="inspector.id"
                                :value="inspector.id">
                                {{ inspector.name | titlecase }}
                            </simple-dropdown-option>
                        </template>
                    </simple-dropdown>

                    <group-toggle-select
                        @input="fetchInspections"
                        v-model=searchParams.statuses_filter
                        :options="['scheduled', 'uploaded', 'completed']">
                    </group-toggle-select>

                    <simple-text-search-input
                        text-search-placeholder="eg. 100 Example Road"
                        text-search-label="Search Asset or Tenant"
                        v-model="searchParams.filter_text"
                        @input="fetchInspections"
                    ></simple-text-search-input>

                    <span v-if="activeFiltersCount > 0"
                          class="h-6 ml-4 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-500 text-white">
                        <span>{{ activeFiltersCount + " Active Filter" | pluralizeOnlyS(activeFiltersCount) }} </span>

                        <button @click.prevent="clearFilters(); fetchInspections();" title="Clear All Filters"
                                class="flex ml-2 cursor-pointer">
                            <icon-x-circle class="w-3 h-3 text-white hover:text-indigo-800 transition duration-300"></icon-x-circle>
                        </button>
                    </span>
                </div>
            </div>

            <div slot="expanded-filters-container" class="bg-white flex justify-around p-8">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div class="block sm:hidden col-span-2 lg:col-span-1">
                        <group-toggle-select
                            @input="fetchInspections"
                            v-model=searchParams.statuses_filter
                            :options="['scheduled', 'uploaded', 'completed']">
                        </group-toggle-select>
                    </div>

                    <div class="block sm:hidden col-span-2 lg:col-span-1">
                        <simple-dropdown origin="top-left"
                                         :label="selectedInspector ? selectedInspector.name : 'All Inspectors'"
                                         v-model="searchParams.inspector_id"
                                         @input="fetchInspections">
                            <template slot="buttonOptions">
                                <div class="py-1" role="none">
                                    <simple-dropdown-option
                                        :key="null"
                                        :value="undefined">
                                        All Inspectors
                                    </simple-dropdown-option>
                                    <simple-dropdown-option
                                        v-for="inspector in inspectors"
                                        :key="inspector.id"
                                        :value="inspector.id">
                                        {{ inspector.name }}
                                    </simple-dropdown-option>
                                </div>
                            </template>
                        </simple-dropdown>
                    </div>

                    <div class="block sm:hidden col-span-2 lg:col-span-1">
                        <simple-text-search-input
                            text-search-placeholder="eg. 100 Example Road"
                            text-search-label="Search Asset or Tenant"
                            v-model="searchParams.filter_text"
                            @input="fetchInspections">
                        </simple-text-search-input>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Select Date Range</label>
                        <el-date-picker
                            v-model="searchParams.date_range_filter"
                            :picker-options="datePickerOptions"
                            value-format="yyyy-MM-dd"
                            type="daterange"
                            range-separator="-"
                            start-placeholder="From date"
                            end-placeholder="To date">
                        </el-date-picker>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Inspection Type</label>
                        <el-select v-model="searchParams.types_filter" class="w-100" multiple>
                            <el-option
                                class="text-gray-800 py-0 flex items-center m-0"
                                v-for="type in inspectionTypes"
                                :key="type"
                                :label="type | capitalize"
                                :value="type">
                            </el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Asset Group</label>
                        <remote-select
                            ref="assetGroupRemoteSelect"
                            @focus="searchAssetGroups"
                            :remote-method="searchAssetGroups"
                            @input="setAssetGroupFilter"
                            :data="assetGroups"
                            :loading="loading"
                            item-label="name"
                            item-key="id"
                            placeholder="Search asset groups">
                            <template slot-scope="props">
                                {{ props.item.name }}
                            </template>
                        </remote-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Online Reports</label>
                        <el-select v-model="searchParams.online_report_filter" class="w-100" clearable>
                            <el-option class="text-gray-800 py-1 flex items-center" key="link not generated" label="Link Not Generated"
                                       value="link not generated"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="link generated but not sent" label="Link Generated But Not Sent"
                                       value="link generated but not sent"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="link sent" label="Link Sent" value="link sent"></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Signed</label>
                        <el-select v-model="searchParams.signatures_filter" class="w-100" multiple>
                            <el-option
                                class="text-gray-800 py-1 flex items-center"
                                v-for="option in signatureFilterOptions"
                                :key="option.key"
                                :label="option.label"
                                :value="option.key">
                            </el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Recommendations</label>
                        <el-select v-model="searchParams.recommendations_filter" class="w-100" clearable>
                            <el-option class="text-gray-800 py-1 flex items-center" key="has recommendations" label="Has Recommendations"
                                       value="has recommendations"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="no recommendations" label="No Recommendations"
                                       value="no recommendations"></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Tickets</label>
                        <el-select v-model="searchParams.tickets_filter" class="w-100" clearable>
                            <el-option class="text-gray-800 py-1 flex items-center" key="has tickets" label="Has Tickets" value="has tickets"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="has no tickets" label="Has No Tickets" value="has no tickets"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="has unresolved tickets" label="Has Unresolved Tickets"
                                       value="has unresolved tickets"></el-option>
                            <el-option class="text-gray-800 py-1 flex items-center" key="has open tickets" label="Has Open Tickets" value="has open tickets"></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1">
                        <label class="block font-medium">Portfolio</label>
                        <el-select v-model="searchParams.portfolio_ids_filter" class="w-100" filterable multiple clearable>
                            <el-option
                                v-for="portfolio in portfolioFilterOptions"
                                :key="portfolio.id"
                                :label="portfolio.name | titlecase"
                                :value="portfolio.id">
                            </el-option>
                        </el-select>
                    </div>

                    <div class="col-span-2 lg:col-span-1 flex flex-col justify-center" v-if="userHasRole('Support')">
                        <div>&nbsp;</div> <!-- empty div to force center -->
                        <checkbox v-model="searchParams.from_migration_filter" label="Migrated From Another Team"></checkbox>
                    </div>
                </div>
            </div>
        </expandable-filters-container>

        <div class="mt-4 overflow-hidden shadow sm:mx-0 rounded-lg">
            <div v-if="!inspectionListLoading && mutableInspectionsList.length <= 0"
                 class="flex justify-center items-center w-full p-20">
                <div class="flex flex-col items-center">
                    <h2 class="text-xl font-normal text-gray-400 my-4 select-none">No matching inspections found</h2>
                </div>
            </div>

            <div v-else-if="inspectionListLoading"
                 class="flex justify-center items-center w-full p-20">
                <div class="flex flex-col items-center">
                    <h2 class="text-xl font-normal text-gray-400 my-4 select-none flex align-items-center">
                        <icon-refresh class="h-8 w-8 text-indigo-600 animate-spin mr-2"/>
                        Loading...
                    </h2>
                </div>
            </div>

            <table v-else-if="mutableInspectionsList.length > 0" class="min-w-full divide-y divide-gray-300">
                <thead class="bg-white">
                    <tr class="select-none">
                        <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-medium text-gray-900">
                            Asset /
                            <span class="hidden lg:inline-block text-indigo-600">Tenant</span>
                            <span class="inline-block lg:hidden text-indigo-600">Inspection Details</span>
                        </th>
                        <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-black lg:table-cell">
                            <div class="flex align-items-center">
                                <sortable-column-header
                                    class="-ml-2 flex align-items-center"
                                    v-model="searchParams.sort.date"
                                    field-name="date"
                                    :sort-direction="searchParams.sort.date"
                                    icon-colour="text-black"
                                    :disable-default="true"
                                    @change="updateSorting">
                                    <template slot="right">
                                        Date /
                                    </template>
                                </sortable-column-header>
                                <span class="text-gray-400"> &nbsp;Type </span>
                            </div>
                        </th>
                        <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-gray-900 lg:table-cell">
                            Inspector / <span class="text-indigo-600">Status</span>
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-medium text-yellow-600">
                            <span v-if="hasFeature('feat_online_inspection_report')">Report Sent / </span>
                            <span class="text-gray-800">Recommendations / </span>
                            <span class="text-gray-400">Signatures</span>
                        </th>
                        <th class="hidden sm:table-cell" scope="col"></th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="inspection in mutableInspectionsList"
                        class="select-none odd:bg-gray-100">
                        <!-- MOBILE STYLING -->
                        <td class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
                            <div class="text-lg sm:text-sm">
                                <div class="block cursor-pointer"
                                     @click.prevent="openLinkInNewTab('/assets/' + inspection.asset_id)">{{ inspection.asset_name }}
                                </div>
                                <div class="block text-indigo-600">{{ inspection.tenant_name ? inspection.tenant_name : "No Tenant" }}</div>
                            </div>

                            <dl class="font-normal lg:hidden text-lg sm:text-sm">
                                <dd class="mt-1 text-gray-700">{{ inspection.date | localeUnawareDatetime }}</dd>
                                <dd class="mt-1 text-gray-500">{{ inspection.type }}
                                    <span
                                        class="relative inline-flex items-center px-2 py-0 rounded bg-white text-base font-medium transition duration-300"
                                        :class="getStatusBadgeClasses(inspection.status)"
                                    >{{ inspection.status }}
                                    </span>
                                </dd>
                            </dl>
                        </td>

                        <!-- DESKTOP STYLING -->
                        <td class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell">
                            <div>
                                <div class="block flex items-center gap-1">{{ inspection.date | localeUnawareDatetime }}
                                    <el-tooltip v-if="inspection.is_copied"
                                                :content="'Copied from ' + inspection.origin_inspection_type + ' inspection dated ' +
                                                inspection.origin_inspection_date"
                                                placement="top">
                                        <icon-copy class="inline w-5 h-5 ml-2">
                                        </icon-copy>
                                    </el-tooltip>

                                    <span v-if="inspection.replication_details" data-toggle="tooltip" data-original-title="replicated from another team"
                                          class="mdi mdi-link-variant text-gray-400 text-xs mb-1">
                                        replication
                                    </span>
                                </div>
                                <div class="block text-gray-400">{{ inspection.type }} Inspection</div>
                            </div>
                        </td>

                        <td class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell">
                            <div class="-mt-1">
                                <div class="flex items-center gap-2">
                                    {{ inspection.inspector_name }}
                                    <div v-if="!(inspection.user_portfolio_access)">
                                        <el-tooltip :content="'Inspector does not have access to asset’s portfolio.'" placement="top">
                                            <badge class="bg-red-100 text-red-600">
                                                No portfolio access
                                            </badge>
                                        </el-tooltip>
                                    </div>
                                </div>

                                <div class="flex items-center">
                                    <badge :class="getStatusBadgeClasses(inspection.status)">
                                        {{ inspection.status | titlecase }}
                                    </badge>
                                </div>
                            </div>
                        </td>

                        <!-- LAST COLUMN IS MOBILE AND DESKTOP STYLING-->
                        <td class="px-3 py-4 text-sm flex flex-wrap items-center gap-4 sm:pt-11 lg:pt-6">
                            <inspection-online-report-badge class="order-0 w-32 flex justify-end"
                                                            :inspection="inspection"
                                                            @update-online-report-status="updateOnlineReportStatus">
                            </inspection-online-report-badge>

                            <button @click="showActionSummaryModal(inspection)" class="order-2 md:order-1">
                                <icon-hammer-and-wrench class="w-5 h-5"
                                                        :class="[inspection.has_action_items === 0 ? 'text-gray-300' : '']">
                                </icon-hammer-and-wrench>
                            </button>

                            <inspection-signatures-list :inspection="inspection" custom-class="w-6 h-6 md:w-8 md:h-8 w-6 h-6"
                                                        class="order-1 md:order-2"></inspection-signatures-list>

                            <div class="sm:hidden flex items-end justify-start order-3">
                                <a :href="'/inspections/' + inspection.id" class="text-indigo-600 font-medium hover:text-indigo-500 text-lg sm:text-base">View</a>
                            </div>
                        </td>

                        <td class="hidden sm:table-cell py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 order-3">
                            <a :href="'/inspections/' + inspection.id" class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <simple-pagination
            v-if="paginationData && paginationData.total > 0"
            :meta="paginationData"
            per-page="10"
            :current-page="paginationData.current_page"
            padding="6"
            @page-changed="fetchInspections">
        </simple-pagination>

        <inspection-actions-summary v-if="inspectionActionSummaryVisible"
                                    max-width="sm:max-w-lg"
                                    :show="inspectionActionSummaryVisible"
                                    :inspection="selectedInspection"
                                    @close="setSelectedInspection(null); inspectionActionSummaryVisible=false">
        </inspection-actions-summary>

        <inspection-create-form
            ref="inspectionCreateForm"
            :inspection-types="inspectionTypes"
            @inspection-created="fetchInspections">
        </inspection-create-form>
    </div>
</template>

<script>
import AssetGroupSearch from "../../mixins/asset-group-search";
import ExpandableFiltersContainer from "../library/ExpandableFiltersContainer";
import InspectionCreateMixin from "../../mixins/create-inspection-mixin";
import SVGInspectionListEmpty from "../library/svgs/SVGInspectionListEmpty";
import GroupToggleSelect from "../library/GroupToggleSelect";
import InspectionActionsSummary from "./InspectionActionsSummary";
import { DATE_RANGE_SHORTCUTS_FULL } from "../../mixins/DateRangeShortcuts";
import InspectionSignaturesList from "./InspectionSignaturesList.vue";
import ExportFileMixin from "../../mixins/export-file";

export default {
    name: "InspectionList",

    mixins: [AssetGroupSearch, InspectionCreateMixin, ExportFileMixin],

    components: {
        InspectionSignaturesList,
        InspectionActionsSummary,
        GroupToggleSelect,
        ExpandableFiltersContainer,
        SVGInspectionListEmpty
    },

    data () {
        return {
            activeFiltersCount: 0,
            mutableInspectionsList: [],
            inspectionListLoading: true,
            inspectionTypes: [],
            paginationData: {},
            inspectors: [],
            selectedInspection: null,
            inspectionActionSummaryVisible: false,
            searchParams: {
                archived_filter: false,
                filter_text: "",
                statuses_filter: ["scheduled", "uploaded"],
                inspector_id: null,
                overdue_filter: false,
                date_range_filter: null,
                types_filter: [],
                asset_group_filter: null,
                signatures_filter: [],
                recommendations_filter: null,
                tickets_filter: null,
                online_report_filter: null,
                portfolio_ids_filter: [],
                from_migration_filter: false,
                sort: {"date": "desc"}
            },
            signatureFilterOptions: [
                {key: "not_signed_by_tenant", label: "Not signed by tenant"},
                {key: "not_signed_by_landlord", label: "Not signed by landlord"},
                {key: "not_signed_by_agent", label: "Not signed by agent"},
                {key: "not_signed_by_inspector", label: "Not signed by inspector"},
                {key: "not_signed_by_other", label: "Not signed by other"},
                {key: "signed_by_tenant", label: "Signed by tenant"},
                {key: "signed_by_landlord", label: "Signed by landlord"},
                {key: "signed_by_agent", label: "Signed by agent"},
                {key: "signed_by_inspector", label: "Signed by inspector"},
                {key: "signed_by_other", label: "Signed by other"},
            ],
            datePickerOptions: {
                shortcuts: DATE_RANGE_SHORTCUTS_FULL
            },
        };
    },

    created () {
        let url = new URL(window.location.href);

        if (url.searchParams.get("status_filter")) {
            this.$set(this.searchParams, "statuses_filter", []);
            this.searchParams.statuses_filter.push(url.searchParams.get("status_filter"));
        }

        if (url.searchParams.get("selected_inspector")) {
            this.$set(this.searchParams, "inspector_id", url.searchParams.get("selected_inspector"));
        }

        if (url.searchParams.get("overdue_filter")) {
            this.$set(this.searchParams, "overdue_filter", Boolean(Number(url.searchParams.get("overdue_filter"))));
        }

        if (url.search.includes("created_at_date_range")) {
            this.$set(this.searchParams, "date_range_filter", []);
            this.searchParams.date_range_filter.push(url.searchParams.get("created_at_date_range[0]"));
            this.searchParams.date_range_filter.push(url.searchParams.get("created_at_date_range[1]"));
        }

        this.fetchUsers();
        this.fetchInspections();
        this.fetchInspectionTypes();
    },

    computed: {
        selectedFiltersCount () {
            let count = 0;
            if (Object.keys(this.searchParams).length > 0) {
                if (this.searchParams.filter_text) {count++;}
                if (this.searchParams.inspector_id) {count++;}
                if (this.searchParams.overdue_filter) {count++;}
                if (!_.isEqual(_.sortBy(this.searchParams.statuses_filter), _.sortBy(["scheduled", "uploaded"]))) {count++;}
                if (this.searchParams.date_range_filter && this.searchParams.date_range_filter.length !== 0) {count++;}
                if (this.searchParams.types_filter.length !== 0) {count++;}
                if (this.searchParams.asset_group_filter) {count++;}
                if (this.searchParams.signatures_filter?.length !== 0) {count++;}
                if (this.searchParams.recommendations_filter) {count++;}
                if (this.searchParams.tickets_filter) {count++;}
                if (this.searchParams.online_report_filter) {count++;}
                if (this.searchParams.portfolio_ids_filter.length !== 0) {count++;}
                if (this.searchParams.from_migration_filter) {count++;}
            }
            return count;
        },

        selectedInspector () {
            return this.searchParams ? this.inspectors.find(inspector => inspector.id === this.searchParams.inspector_id) : null;
        },

        portfolioFilterOptions () {
            return _.sortBy(this.state.user.portfolios, "name");
        },
    },

    methods: {
        setSelectedInspection (inspection) {
            this.selectedInspection = inspection;
        },


        showActionSummaryModal (inspection) {
            this.setSelectedInspection(inspection);
            this.inspectionActionSummaryVisible = true;
        },

        updateOnlineReportStatus (inspectionId, value) {
            // Value is either "not generated" or "generated" or "shared"
            let index = this.mutableInspectionsList.findIndex(i => i.id === inspectionId);
            if (index !== -1) {
                this.$set(this.mutableInspectionsList[index], "online_report_status", value);
            }
        },

        getStatusBadgeClasses (status) {
            switch (status) {
                case "uploaded":
                    return "bg-yellow-100 text-yellow-600";
                case "completed":
                    return "bg-green-100 text-green-600";
                default:
                    return "bg-indigo-100 text-indigo-600";
            }
        },

        openLinkInNewTab (url) {
            window.open(url, "_blank");
        },

        scheduleInspection () {
            this.$refs.inspectionCreateForm.showInspectionCreateForm();
        },

        fetchInspections: _.debounce(function (paginationSettings = null) {
            this.inspectionListLoading = true;
            this.setPaginationAndSort(paginationSettings);
            axios.get("/inspections/all", {
                params: this.searchParams
            }).then((response) => {
                const {data, ...paginationData} = response.data;
                this.mutableInspectionsList = data;
                this.paginationData = paginationData.meta;
                this.activeFiltersCount = this.selectedFiltersCount;
                this.inspectionListLoading = false;
            }).catch((error) => {
                toastr.error("Could not fetch inspections:" + error);
                this.inspectionListLoading = false;
            });
        }, 300),

        fetchUsers () {
            axios.get("/users", {
                params: {
                    status: "active",
                    per_page: 200
                }
            }).then((response) => {
                this.inspectors = response.data.data;
            }).catch(error => {
                toastr.error("Could not fetch inspector list" + error.message);
                this.loading = false;
            });
        },

        setAssetGroupFilter (id) {
            this.$set(this.searchParams, "asset_group_filter", id);
        },

        setPaginationAndSort (paginationSettings = null) {
            this.$set(this.searchParams, "sort", {
                date: this.searchParams?.sort?.date ? this.searchParams.sort.date : "default",
            });
            this.$set(this.searchParams, "page", paginationSettings?.pageNumber ? paginationSettings.pageNumber : 1);
            this.$set(this.searchParams, "per_page", paginationSettings?.perPage ? paginationSettings.perPage : 10);
        },

        updateSorting (sortParam, direction) {
            this.$set(this.searchParams.sort, sortParam, direction);
            this.fetchInspections();
        },

        fetchInspectionTypes () {
            axios.get("/settings/inspections/inspection-types")
            .then(response => this.inspectionTypes = response.data)
            .catch(error => toastr.error("Could not fetch inspection types for filter. " + error));
        },

        toggleArchivedInspections () {
            this.$set(this.searchParams, "archived_filter", !this.searchParams.archived_filter);
            this.fetchInspections();
        },

        exportInspections () {
            this.setPaginationAndSort();
            this.exportData("/inspections/export", this.searchParams, this.$dayjs().format("DD-MM-YYYY") + " Inspections Export.xlsx");
        },

        clearFilters () {
            // purposefully don't clear the archived & overdue filter
            this.$set(this.searchParams, "filter_text", "");
            this.$set(this.searchParams, "statuses_filter", ["scheduled", "uploaded"]);
            this.$set(this.searchParams, "inspector_id", null);
            this.$set(this.searchParams, "date_range_filter", null);
            this.$set(this.searchParams, "types_filter", []);
            this.$set(this.searchParams, "asset_group_filter", null);
            this.$refs.assetGroupRemoteSelect.clear();
            this.$set(this.searchParams, "signatures_filter", []);
            this.$set(this.searchParams, "recommendations_filter", null);
            this.$set(this.searchParams, "tickets_filter", null);
            this.$set(this.searchParams, "online_report_filter", null);
            this.$set(this.searchParams, "portfolio_ids_filter", []);
            this.$set(this.searchParams, "from_migration_filter", false);

            this.fetchInspections();
        },
    },
};
</script>
